import React, { ReactNode } from 'react';
import styles from './AdminGrid.module.scss';
import Header from '@src/components/header/Header';
import Footer from '@src/components/footer/Footer';
import { NavLink } from 'react-router-dom';
import IconLeft from '@src/assets/icons/chevron_left_circled.svg?react';
import FooterPage from '../footer-page/FooterPage';

type AdminGridWithBackButtonProps = {
  title: string;
  subtitle?: string | ReactNode;
  children: ReactNode;
  headerActions?: ReactNode;
  filterSection?: ReactNode;
  advancedFilters?: ReactNode;
  resultsCount?: number;
  resultsCountLabel?: string;
  backButtonLink: string;
};

/**
 * AdminGridWithBackButton - A shared grid component for admin pages with a back button
 * 
 * This component provides a consistent layout for admin pages with:
 * - Header and footer
 * - Back button
 * - Title and subtitle
 * - Optional header actions (buttons, etc.)
 * - Filter section
 * - Advanced filters
 * - Results count
 * - Main content area
 */
const AdminGridWithBackButton: React.FC<AdminGridWithBackButtonProps> = ({
  title,
  subtitle,
  children,
  headerActions,
  filterSection,
  advancedFilters,
  resultsCount,
  resultsCountLabel = 'Results',
  backButtonLink
}) => {
  return (
    <>
      <Header />
      <div className={styles.page}>
        <div className={styles.head}>
          <div className={styles.titleSection}>
            <div className={styles.titleWithBack}>
              <NavLink to={backButtonLink} className={styles.backLink}>
                <IconLeft/>
              </NavLink>
              <div>
                <div className={styles.title}>{title}</div>
                {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
              </div>
            </div>
          </div>
          {headerActions && (
            <div className={styles.headerActions}>
              {headerActions}
            </div>
          )}
        </div>

        {filterSection && (
          <div className={styles.filters}>
            {React.isValidElement(filterSection) && resultsCount !== undefined
              ? React.cloneElement(filterSection as React.ReactElement<any>, {
                  resultsCount,
                  resultsCountLabel
                })
              : filterSection}
          </div>
        )}

        {advancedFilters && (
          <div className={styles.advancedFilters}>
            <div className={styles.advancedFiltersContent}>
              {advancedFilters}
            </div>
            {resultsCount !== undefined && (
              <div className={styles.resultsCount}>
                <span className={styles.resultsCountNumber}>{resultsCount}</span> {resultsCountLabel}
              </div>
            )}
          </div>
        )}

        <div className={styles.content}>
          {children}
        </div>
        <FooterPage />
      </div>
      {/* <Footer /> */}
    </>
  );
};

export default AdminGridWithBackButton;
